import React from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { DialogTitle } from '@/components/ui/SossietyDialog';

interface SossietyDialogHeaderProps {
  title: string;
  children?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  description?: React.ReactNode;
  closeButtonSpacing?: string;
}

interface SossietyDialogIconProps {
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

interface SossietyDialogCloseProps {
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
  [key: string]: any; // Allow any additional props like data-testid
}

export const SossietyDialogClose: React.FC<SossietyDialogCloseProps> = ({
  onClick,
  className,
  disabled = false,
  'data-testid': dataTestId = 'training-notes-modal-close-button',
  ...restProps
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className={cn(
        "rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none border-0 focus:ring-0 focus:border-0",
        className
      )}
      {...restProps}
    >
      <X className="h-6 w-6" />
      <span className="sr-only">Close</span>
    </button>
  );
};

export const SossietyDialogIcon: React.FC<SossietyDialogIconProps> = ({
  onClick,
  children,
  className,
  disabled = false
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none border-0 focus:ring-0 focus:border-0",
        className
      )}
    >
      {children}
    </button>
  );
};

export const SossietyDialogHeader: React.FC<SossietyDialogHeaderProps> = ({
  title,
  children,
  className,
  titleClassName,
  description,
  closeButtonSpacing = 'right-6'
}) => {
  return (
    <div className={cn("relative mb-4 !space-y-0", className)}>
      {/* Title and close button container - using flex for alignment */}
      <div className="flex items-center justify-center relative pt-4 pb-4">
        <DialogTitle className={cn(
          "text-xl font-semibold text-black text-center leading-none tracking-normal flex-1",
          titleClassName
        )}>
          {title}
        </DialogTitle>
        
        {/* Icons container - aligned with title center */}
        <div className={cn("absolute flex items-center gap-0.5", closeButtonSpacing)}>
          {children}
        </div>
      </div>
      
      {/* Description - if provided */}
      {description && (
        <div className="mt-2">
          {description}
        </div>
      )}
    </div>
  );
}; 